// validation.js  (put next to CaseStudyForm.jsx)
// Centralized validation rules + helpers for the Procman AI Case Study CMS form.
// Frontend/admin-panel only. Limits are based on the live case-study detail page
// layout (hero H1 = ~3 lines, 5 metric columns, 4 phase cards, etc.).
//
// Error-key convention (same keys used by CaseStudyForm to read errors):
//   basic fields        -> "title", "slug", "description", "author_name", "company_name", ...
//   section fields      -> "results_achieved.title", "faqs.title", ...
//   repeater count      -> "results_achieved.items", "before_after.before", ...
//   repeater item field -> "results_achieved.items[0].value", "faqs.items[2].answer", ...

// ---------------------------------------------------------------------------
// TEXT RULES  { label, min, max, required, help }
// ---------------------------------------------------------------------------
export const TEXT_RULES = {
  // ---- BASIC ----
  title: { label: "Case study title", min: 10, max: 80, required: true, help: "Hero H1 — keep it to ~3 lines." },
  slug: { label: "URL slug", min: 3, max: 100, required: true },
  description: { label: "Short description", min: 30, max: 300, required: true, help: "Shown under the hero heading and on listing cards." },
  author_name: { label: "Author name", min: 2, max: 40, required: false },
  company_name: { label: "Company name", min: 2, max: 60, required: false },

  // ---- RESULTS ACHIEVED ----
  "results_achieved.title": { label: "Section title", min: 3, max: 40, required: true },
  "results_achieved.subtitle": { label: "Subtitle", min: 0, max: 80, required: false },
  "results_achieved.item.value": { label: "Metric value", min: 1, max: 12, required: true, help: "e.g. 21%, 30 Days, 10,60,791" },
  "results_achieved.item.label": { label: "Metric label", min: 2, max: 30, required: true },

  // ---- COMPANY OVERVIEW ----
  "company_overview.title": { label: "Section title", min: 3, max: 40, required: true },
  "company_overview.description": { label: "Company overview description", min: 20, max: 600, required: true },
  "company_overview.industry_label": { label: "Industry label", min: 2, max: 20, required: true },
  "company_overview.industry_value": { label: "Industry value", min: 2, max: 40, required: true },
  "company_overview.employees": { label: "Number of employees", min: 1, max: 20, required: true },
  "company_overview.annual_spend": { label: "Annual spend", min: 1, max: 20, required: true },

  // ---- CHALLENGE / SOLUTION ----
  "challenge.title": { label: "Challenge section title", min: 3, max: 40, required: true },
  "challenge.item.title": { label: "Challenge title", min: 5, max: 90, required: true },
  "challenge.item.description": { label: "Challenge description", min: 0, max: 200, required: false },
  "solution.title": { label: "Solution section title", min: 3, max: 40, required: true },
  "solution.item.title": { label: "Solution title", min: 5, max: 90, required: true },
  "solution.item.description": { label: "Solution description", min: 0, max: 200, required: false },

  // ---- IMPLEMENTATION JOURNEY ----
  "implementation_journey.title": { label: "Section title", min: 3, max: 40, required: true },
  "implementation_journey.subtitle": { label: "Subtitle", min: 0, max: 80, required: false },
  "implementation_journey.item.phase": { label: "Phase label", min: 3, max: 15, required: true, help: "e.g. Phase 1" },
  "implementation_journey.item.title": { label: "Phase title", min: 3, max: 50, required: true },
  "implementation_journey.item.description": { label: "Description", min: 10, max: 250, required: true },

  // ---- BEFORE / AFTER ----
  "before_after.title": { label: "Section title", min: 5, max: 60, required: true },
  "before_after.item.title": { label: "Item title", min: 3, max: 60, required: true },
  "before_after.item.description": { label: "Item description", min: 0, max: 150, required: false },

  // ---- CUSTOMER REVIEW ----
  "customer_review.testimonial": { label: "Customer testimonial", min: 20, max: 400, required: true },
  "customer_review.reviewer_name": { label: "Reviewer name", min: 2, max: 40, required: true },
  "customer_review.designation": { label: "Designation", min: 2, max: 50, required: false },
  "customer_review.company": { label: "Company", min: 2, max: 50, required: false },

  // ---- FAQs ----
  "faqs.title": { label: "Section title", min: 3, max: 50, required: true },
  "faqs.item.question": { label: "Question", min: 5, max: 100, required: true },
  "faqs.item.answer": { label: "Answer", min: 5, max: 400, required: true },
};

// ---------------------------------------------------------------------------
// REPEATER COUNT RULES  (keyed by the error path of the list)
// ---------------------------------------------------------------------------
export const REPEATER_RULES = {
  "results_achieved.items": { min: 3, max: 5, noun: "metric" },
  "challenge.items": { min: 1, max: 6, noun: "challenge" },
  "solution.items": { min: 1, max: 6, noun: "solution" },
  "implementation_journey.items": { min: 2, max: 6, noun: "phase" },
  "before_after.before": { min: 2, max: 6, noun: "item" },
  "before_after.after": { min: 2, max: 6, noun: "item" },
  "faqs.items": { min: 3, max: 10, noun: "FAQ" },
};

// ---------------------------------------------------------------------------
// IMAGE / FILE SPECS  (og_image & twitter_image are handled by checkSocialImage)
// ---------------------------------------------------------------------------
const IMG = ["image/jpeg", "image/png", "image/webp"];

export const IMAGE_SPECS = {
  banner_image: {
    formats: IMG,
    maxSizeMB: 3,
    minWidth: 1200,
    aspect: 1600 / 600,
    aspectTolerance: 0.35,
    help: "Recommended 1600×600, JPG/PNG/WebP, under 3MB.",
  },
  featured_image: {
    formats: IMG,
    maxSizeMB: 2,
    minWidth: 600,
    help: "Listing image, min 600px wide, under 2MB.",
  },
  company_logo: {
    formats: [...IMG, "image/svg+xml"],
    maxSizeMB: 1,
    help: "Square or transparent PNG/SVG, under 1MB.",
  },
  case_study_pdf: {
    formats: ["application/pdf"],
    maxSizeMB: 10,
    help: "PDF only, under 10MB.",
  },
};

// ---------------------------------------------------------------------------
// HELPERS
// ---------------------------------------------------------------------------

/** `strict=false` => don't complain about empty/required yet (used while typing / saving draft). */
export function validateText(value, rules, label, strict = true) {
  if (!rules) return null;
  const name = label || rules.label || "This field";
  const len = (value ?? "").toString().trim().length;
  if (len === 0) return strict && rules.required ? `${name} is required.` : null;
  if (rules.min && len < rules.min) return `${name} needs at least ${rules.min} characters (${len}/${rules.min}).`;
  if (rules.max && len > rules.max) return `${name} is over the limit (${len}/${rules.max}).`;
  return null;
}

export function validateSlug(value, strict = true) {
  const v = (value ?? "").trim();
  if (!v) return strict ? "URL slug is required." : null;
  if (v.length < TEXT_RULES.slug.min) return `URL slug needs at least ${TEXT_RULES.slug.min} characters.`;
  if (v.length > TEXT_RULES.slug.max) return `URL slug is over the limit (${v.length}/${TEXT_RULES.slug.max}).`;
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(v)) return "Slug can only contain lowercase letters, numbers and single hyphens.";
  return null;
}

export function validateDate(value) {
  if (!value) return null; // optional
  return Number.isNaN(Date.parse(value)) ? "Enter a valid date." : null;
}

export function validateRepeaterCount(items, rules, strict = true) {
  if (!rules) return null;
  const count = items?.length || 0;
  if (strict && rules.min && count < rules.min) {
    return `Add at least ${rules.min} ${rules.noun}${rules.min > 1 ? "s" : ""} (currently ${count}).`;
  }
  if (rules.max && count > rules.max) {
    return `No more than ${rules.max} ${rules.noun}${rules.max > 1 ? "s" : ""} allowed — remove ${count - rules.max} (currently ${count}).`;
  }
  return null;
}

// ---- images / files ----
export function validateImageFile(file, spec) {
  const errors = [];
  if (!spec.formats.includes(file.type)) {
    errors.push(
      `Unsupported format. Use ${spec.formats.map((f) => f.split("/")[1].replace("+xml", "").toUpperCase()).join(", ")}.`
    );
  }
  const sizeMB = file.size / (1024 * 1024);
  if (sizeMB > spec.maxSizeMB) {
    errors.push(`File is ${sizeMB.toFixed(1)}MB — max allowed is ${spec.maxSizeMB}MB.`);
  }
  return errors;
}

export function getImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read image dimensions."));
    };
    img.src = url;
  });
}

export function validateImageDimensions({ width, height }, spec) {
  const errors = [];
  if (spec.minWidth && width < spec.minWidth) errors.push(`Width ${width}px is below the minimum ${spec.minWidth}px.`);
  if (spec.maxWidth && width > spec.maxWidth) errors.push(`Width ${width}px is above the maximum ${spec.maxWidth}px.`);
  if (spec.minHeight && height < spec.minHeight) errors.push(`Height ${height}px is below the minimum ${spec.minHeight}px.`);
  if (spec.maxHeight && height > spec.maxHeight) errors.push(`Height ${height}px is above the maximum ${spec.maxHeight}px.`);
  if (spec.aspect) {
    const ratio = width / height;
    const diff = Math.abs(ratio - spec.aspect) / spec.aspect;
    if (diff > (spec.aspectTolerance ?? 0.1)) {
      errors.push(`Aspect ratio (${ratio.toFixed(2)}:1) doesn't match the expected ~${spec.aspect.toFixed(2)}:1 — image may get cropped oddly.`);
    }
  }
  return errors;
}

/** Full check (format, size, dimensions). Returns an array of error strings ([] = OK). */
export async function validateImageBeforeUpload(file, fieldName) {
  const spec = IMAGE_SPECS[fieldName];
  if (!spec) return [];
  const syncErrors = validateImageFile(file, spec);
  if (syncErrors.length) return syncErrors;
  // PDFs / SVGs: no pixel dimensions to check
  if (file.type === "application/pdf" || file.type === "image/svg+xml") return [];
  try {
    return validateImageDimensions(await getImageDimensions(file), spec);
  } catch (e) {
    return [e.message || "Could not validate image dimensions."];
  }
}

// ---------------------------------------------------------------------------
// WHOLE-FORM VALIDATION
// Returns { [errorKey]: message }. Sections whose "enabled" toggle is OFF are skipped.
// strict=false -> only "bad value" errors (too long/short, invalid) — no required/min-count.
// strict=true  -> everything (use on Publish).
// ---------------------------------------------------------------------------
export function validateCaseStudyForm(form, { strict = true } = {}) {
  const errors = {};
  const put = (key, msg) => {
    if (msg) errors[key] = msg;
  };
  const text = (key, value, ruleKey = key) =>
    put(key, validateText(value, TEXT_RULES[ruleKey], undefined, strict));
  const list = (path, items, ruleKey, fields) => {
    put(path, validateRepeaterCount(items, REPEATER_RULES[path], strict));
    (items || []).forEach((item, i) =>
      fields.forEach((f) => text(`${path}[${i}].${f}`, item?.[f], `${ruleKey}.${f}`))
    );
  };

  // ---- basic ----
  const other = form.other_data || {};
  text("title", form.title);
  put("slug", validateSlug(form.slug, strict));
  if (strict && !String(form.industry ?? "").trim()) put("industry", "Please select an industry.");
  text("description", form.description);
  text("author_name", other.author_name);
  text("company_name", other.company_name);
  put("published_date", validateDate(other.published_date));

  // ---- detail page sections ----
  const d = form.detail_page || {};
  const on = (s) => d[s] && d[s].enabled !== false;

  if (on("results_achieved")) {
    const s = d.results_achieved;
    text("results_achieved.title", s.title);
    text("results_achieved.subtitle", s.subtitle);
    list("results_achieved.items", s.items, "results_achieved.item", ["value", "label"]);
  }

  if (on("company_overview")) {
    const s = d.company_overview;
    ["title", "description", "industry_label", "industry_value", "employees", "annual_spend"].forEach((k) =>
      text(`company_overview.${k}`, s[k])
    );
  }

  if (on("challenge")) {
    text("challenge.title", d.challenge.title);
    // add "description" here if you re-enable that textarea in the form
    list("challenge.items", d.challenge.items, "challenge.item", ["title"]);
  }

  if (on("solution")) {
    text("solution.title", d.solution.title);
    list("solution.items", d.solution.items, "solution.item", ["title"]);
  }

  if (on("implementation_journey")) {
    const s = d.implementation_journey;
    text("implementation_journey.title", s.title);
    text("implementation_journey.subtitle", s.subtitle);
    list("implementation_journey.items", s.items, "implementation_journey.item", ["phase", "title", "description"]);
  }

  if (on("before_after")) {
    const s = d.before_after;
    text("before_after.title", s.title);
    list("before_after.before", s.before, "before_after.item", ["title"]);
    list("before_after.after", s.after, "before_after.item", ["title"]);
  }

  if (on("customer_review")) {
    const s = d.customer_review;
    ["testimonial", "reviewer_name", "designation", "company"].forEach((k) =>
      text(`customer_review.${k}`, s[k])
    );
    const r = Number(s.rating);
    if (!(r >= 1 && r <= 5)) put("customer_review.rating", "Rating must be between 1 and 5.");
  }

  if (on("faqs")) {
    text("faqs.title", d.faqs.title);
    list("faqs.items", d.faqs.items, "faqs.item", ["question", "answer"]);
  }

  return errors;
}

// ---------------------------------------------------------------------------
// Which tab owns an error key? (used to auto-jump + show red dot on the sidebar)
// ---------------------------------------------------------------------------
const TAB_PREFIXES = [
  ["results_achieved", "results"],
  ["company_overview", "company"],
  ["challenge", "challenge"],
  ["solution", "challenge"],
  ["implementation_journey", "journey"],
  ["before_after", "before_after"],
  ["customer_review", "review"],
  ["faqs", "faqs"],
];

export function getTabForError(key) {
  const hit = TAB_PREFIXES.find(([prefix]) => key.startsWith(prefix));
  return hit ? hit[1] : "basic"; // title, slug, industry, description, author, company_name, date
}